import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";

/**
 * Suggestions - a row of quick-reply / follow-up chips. Clicking one sends it
 * back as the next user turn. Renderer for the K2 `suggestions` block
 * (`docs/contracts/block-registry.md`): the platform's chat `BlockRenderer`
 * passes a `suggestions` block's `props` straight through, so the prop names
 * match the block payload exactly. INTERACTIVE.
 *
 * Block props (`{ prompt?, suggestions, submit_to, receipt? }`):
 *   - `prompt?` - optional lead-in line above the chips ("You could ask:").
 *   - `suggestions` - the chips, each `{ id, label, value? }`. `value` defaults
 *     to `label` (what gets sent back).
 *   - `submit_to` - the destination routing token (mirrors `form.submit_to`);
 *     carried on the emitted submit event.
 *   - `receipt?` - once a chip is chosen, the durable settled state
 *     (`{ outcome, summary, identifiers?, at }`). When present, the settled
 *     receipt renders instead of the live chips, so a history reload shows the
 *     chosen reply rather than re-offering live, un-actioned chips.
 *
 * On click the block emits the shared block-submit event keyed by `block_id` +
 * the chosen chip id + value (see `blockSubmit.tsx`) - the same transport
 * `form` / `approval` use, no new mechanism.
 *
 * Client-only props (never part of the streamed block): `className`, `onSelect`
 * (wired by the block adapter to the submit context).
 *
 * Chips stagger in (layout settles first, opacity ramps after - the house
 * slide-then-fade), disabled under reduced motion. Token-themed
 * (`bg-card`, `border-border`, `hover:border-accent`, ...) with no hardcoded
 * colors. SSR-safe.
 */

/** Source: assistant-ui Suggestion, AI Elements Suggestion, Crayon FollowUpBlock. */
export interface SuggestionChip {
  /** Stable id, sent back as the submit `action`. */
  id: string;
  /** Chip label. */
  label: string;
  /** Value sent back as the next turn. Defaults to `label`. */
  value?: string;
}

const suggestionsVariants = cva("not-prose flex flex-col gap-2");

export interface SuggestionsProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect">,
    VariantProps<typeof suggestionsVariants> {
  /** Optional lead-in line above the chips. */
  prompt?: string;
  /** The chips. Required. */
  suggestions: SuggestionChip[];
  /** Settled state - renders the receipt instead of live chips when present. */
  receipt?: BlockReceipt | unknown;
  /** Selection handler. Wired by the block adapter to the submit context. */
  onSelect?: (chip: SuggestionChip) => void;
  /** Disable interaction (e.g. while the next turn is in flight). */
  disabled?: boolean;
}

export const Suggestions = React.forwardRef<HTMLDivElement, SuggestionsProps>(function Suggestions(
  { className, prompt, suggestions, receipt, onSelect, disabled = false, ...rest },
  ref,
) {
  const settled = readReceipt(receipt);

  if (settled) {
    return (
      <div
        ref={ref}
        className={cn(suggestionsVariants(), className)}
        data-slot="suggestions"
        data-settled="true"
        {...rest}
      >
        <BlockReceiptView receipt={settled} />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(suggestionsVariants(), className)}
      data-slot="suggestions"
      {...rest}
    >
      {prompt ? <p className="text-xs text-muted-foreground">{prompt}</p> : null}
      <div className="flex flex-wrap gap-2" role="group" aria-label={prompt ?? "Suggested replies"}>
        {suggestions.map((chip, i) => (
          <button
            key={chip.id}
            type="button"
            disabled={disabled}
            onClick={() => onSelect?.(chip)}
            style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
            className={cn(
              "inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground",
              "transition-colors hover:border-accent hover:bg-accent/10 hover:text-accent",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              // Slide-then-fade: each chip rises into place then settles; the
              // stagger comes from the per-chip animationDelay above. Disabled
              // under reduced motion.
              "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both",
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
});

Suggestions.displayName = "Suggestions";

export { suggestionsVariants };
export type SuggestionsVariantProps = VariantProps<typeof suggestionsVariants>;
